import { useState, useRef, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setFormState('submitting');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value as string); });
    try {
      const res = await fetch('https://readdy.ai/api/form/d7c9g76lfkej60p1keig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setFormState('success'); form.reset(); setCharCount(0); }
      else setFormState('error');
    } catch { setFormState('error'); }
  };

  return (
    <section id="contact" ref={sectionRef} className="py-24 md:py-32 bg-[#FFF9F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-200 mb-5">
            <i className="ri-mail-send-line text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-xs font-semibold tracking-wider uppercase">{t('contact.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a4a] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('contact.title')}
          </h2>
          <p className="text-[#666] text-base md:text-lg max-w-xl mx-auto leading-relaxed">{t('contact.desc')}</p>
        </div>

        <div
          className={`rounded-3xl overflow-hidden transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{ background: 'linear-gradient(135deg, #1e3a4a 0%, #2d5568 100%)' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left Info */}
            <div className="lg:col-span-2 p-10 md:p-14 flex flex-col justify-between">
              <div>
                <div className="text-amber-400 text-6xl font-serif leading-none mb-6 opacity-60">&ldquo;</div>
                <h3 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t('contact.left.title').split('\n').map((line, i) => (
                    <span key={i}>{line}{i === 0 && <br />}</span>
                  ))}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed mb-10">{t('contact.left.desc')}</p>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 shrink-0">
                    <i className="ri-whatsapp-line text-base"></i>
                  </div>
                  <a
                    href="https://wa.me/6596628319"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white text-sm font-medium hover:text-amber-400 transition-colors duration-200 cursor-pointer"
                  >
                    {t('contact.whatsapp')}
                  </a>
                </div>
                {[
                  { icon: 'ri-mail-line', key: 'contact.email', href: 'mailto:cs@risephoenixtech.com' },
                  { icon: 'ri-map-pin-line', key: 'contact.address', href: null },
                ].map((item) => (
                  <div key={item.key} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-white text-sm font-medium hover:text-amber-400 transition-colors duration-200 cursor-pointer"
                      >
                        {t(item.key)}
                      </a>
                    ) : (
                      <div className="text-white text-sm font-medium">{t(item.key)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3 bg-white p-10 md:p-14">
              {formState === 'success' ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-500 mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h4 className="text-[#1e3a4a] text-2xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {t('contact.form.success.title')}
                  </h4>
                  <p className="text-[#666] text-sm leading-relaxed mb-8">{t('contact.form.success.desc')}</p>
                  <button
                    onClick={() => setFormState('idle')}
                    className="px-6 py-2.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('contact.form.success.again')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#333] text-xs font-semibold mb-2 uppercase tracking-wide">
                        {t('contact.form.name')} <span className="text-red-400">{t('contact.required')}</span>
                      </label>
                      <input type="text" name="name" required placeholder={t('contact.form.name.placeholder')}
                        className="w-full px-4 py-3 rounded-xl bg-[#f8f6f2] border border-transparent focus:border-amber-400 focus:outline-none text-sm text-[#333] placeholder-[#aaa] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[#333] text-xs font-semibold mb-2 uppercase tracking-wide">
                        {t('contact.form.company')}
                      </label>
                      <input type="text" name="company" placeholder={t('contact.form.company.placeholder')}
                        className="w-full px-4 py-3 rounded-xl bg-[#f8f6f2] border border-transparent focus:border-amber-400 focus:outline-none text-sm text-[#333] placeholder-[#aaa] transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#333] text-xs font-semibold mb-2 uppercase tracking-wide">
                        {t('contact.form.email')} <span className="text-red-400">{t('contact.required')}</span>
                      </label>
                      <input type="email" name="email" required placeholder="your@email.com"
                        className="w-full px-4 py-3 rounded-xl bg-[#f8f6f2] border border-transparent focus:border-amber-400 focus:outline-none text-sm text-[#333] placeholder-[#aaa] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[#333] text-xs font-semibold mb-2 uppercase tracking-wide">
                        {t('contact.form.phone')}
                      </label>
                      <input type="tel" name="phone" placeholder={t('contact.form.phone.placeholder')}
                        className="w-full px-4 py-3 rounded-xl bg-[#f8f6f2] border border-transparent focus:border-amber-400 focus:outline-none text-sm text-[#333] placeholder-[#aaa] transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#333] text-xs font-semibold mb-2 uppercase tracking-wide">
                      {t('contact.form.type')}
                    </label>
                    <select name="project_type"
                      className="w-full px-4 py-3 rounded-xl bg-[#f8f6f2] border border-transparent focus:border-amber-400 focus:outline-none text-sm text-[#333] transition-colors cursor-pointer appearance-none">
                      <option value="">{t('contact.form.type.placeholder')}</option>
                      <option value="website">{t('contact.form.type.web')}</option>
                      <option value="mobile_app">{t('contact.form.type.app')}</option>
                      <option value="consulting">{t('contact.form.type.consult')}</option>
                      <option value="maintenance">{t('contact.form.type.maintain')}</option>
                      <option value="other">{t('contact.form.type.other')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#333] text-xs font-semibold mb-2 uppercase tracking-wide">
                      {t('contact.form.message')} <span className="text-red-400">{t('contact.required')}</span>
                    </label>
                    <textarea name="message" required rows={4} maxLength={500}
                      placeholder={t('contact.form.message.placeholder')}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-xl bg-[#f8f6f2] border border-transparent focus:border-amber-400 focus:outline-none text-sm text-[#333] placeholder-[#aaa] transition-colors resize-none" />
                    <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-red-400' : 'text-[#aaa]'}`}>
                      {charCount}/500
                    </div>
                  </div>
                  {formState === 'error' && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                      <i className="ri-error-warning-line"></i>
                      <span>{t('contact.form.error')}</span>
                    </div>
                  )}
                  <button type="submit" disabled={formState === 'submitting' || charCount > 500}
                    className="w-full py-4 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    {formState === 'submitting' ? (
                      <><i className="ri-loader-4-line animate-spin"></i><span>{t('contact.form.submitting')}</span></>
                    ) : (
                      <><span>{t('contact.form.submit')}</span><i className="ri-send-plane-line"></i></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
